import { storeToRefs } from 'pinia';
import useAudioNarration from './useAudioNarration';
import useContent from '../stores/useContent';
import { schemaForTemplate } from './screen-schemas/index';
import {
  resolveJitAllSlotTargets,
  type JitEntryPrefetchContext
} from './jitEntryPrefetch';
import type { NarratableScreen } from './screenAudioText';
import {
  joinAudioJitCacheObjectUrl,
  resolveJitAudioCacheKey
} from './audioJitCacheKey';
import {
  extractAudioAiJitConfig,
  getJitEnabledLocalesFromConfig
} from './audioJitCourseConfig';
import { get } from './SCORM';

/**
 * Author-tool bridge for JiT audio. Exposes `window.ucJit` so the internal
 * Authoring Tool's AuthorAudioJIT page can drive THIS runtime (loaded in a
 * same-origin iframe with `?ucAuthorAudio=1&audioDoc=1`) instead of
 * re-implementing course loading / base+custom merge / translation collation.
 *
 * Everything here reuses the runtime's authoritative state: `useContent().compiled`
 * (merged base+custom from getContent), `useTranslations` (via `resolveText`), and
 * the JiT config / cache logic from `useAudioNarration`.
 *
 * Invoked once from `AppContent.vue` setup, guarded by the `ucAuthorAudio` flag.
 */

export interface UcJitSlot {
  key: string;
  text: string;
}

export interface UcJitScreenGroup {
  screenId: string;
  screenLabel: string;
  slots: UcJitSlot[];
}

export interface UcJitWarmResult {
  source?: 'cache' | 'provider';
  cacheUrl: string;
  costUsd?: number;
  responseTimeMs?: number;
}

export interface UcJitApi {
  ready: true;
  /** Whether audio-ai-jit is enabled for the current course/locale. */
  enabled: boolean;
  locale: string;
  provider?: string;
  voice?: string;
  model?: string;
  voiceLocale?: string;
  /** Languages declared on the course. */
  languages: string[];
  /**
   * Course locales that actually have JiT enabled (per-locale config + voice
   * resolves). The author language dropdown should use this — picking a locale
   * outside it would resolve to `enabled:false`.
   */
  jitLanguages: string[];
  /** All JiT audio slots, grouped by screen, with translations applied. */
  list(): UcJitScreenGroup[];
  /**
   * Synthesise + cache a single slot's text. `allowRead:false` forces
   * regeneration; `allowWrite:false` skips persisting to S3. Both default true.
   */
  warm(
    text: string,
    opts?: { allowRead?: boolean; allowWrite?: boolean }
  ): Promise<UcJitWarmResult>;
  /** Public S3 cache URL for a slot's text (for playback). */
  cacheUrlFor(text: string): Promise<string>;
}

declare global {
  interface Window {
    ucJit?: UcJitApi;
  }
}

export default function useJitAuthorApi() {
  const {
    audioAiJitEnabled,
    audioAiJitConfig,
    audioAiJitCacheBaseUrl,
    resolveText,
    warmNarrationCache
  } = useAudioNarration();
  const contentStore = useContent();
  const { compiled } = storeToRefs(contentStore);

  const currentLocale = (): string =>
    (get('locale') as string) ||
    (compiled.value?.['default-language'] as string) ||
    'en-GB';

  const buildList = (): UcJitScreenGroup[] => {
    const c = compiled.value;
    if (!c) return [];

    const ctx: JitEntryPrefetchContext = {
      compiled: c,
      locale: currentLocale(),
      resolveText
    };

    const groups: UcJitScreenGroup[] = [];
    for (const page of c.pages ?? []) {
      for (const rawScreen of page.screens ?? []) {
        const screen = rawScreen as NarratableScreen;
        if (!screen?.id || screen['dont-read']) continue;
        if (!schemaForTemplate(screen.template as string | undefined)) continue;

        const targets = resolveJitAllSlotTargets(screen, ctx);
        if (targets.length === 0) continue;

        const label =
          (typeof screen.heading === 'string' && screen.heading) ||
          (typeof screen.title === 'string' && screen.title) ||
          screen.id;

        groups.push({
          screenId: screen.id,
          screenLabel: String(label),
          slots: targets.map(t => ({ key: t.key, text: t.text }))
        });
      }
    }
    return groups;
  };

  const cacheUrlFor = async (text: string): Promise<string> => {
    const cfg = audioAiJitConfig.value;
    if (!cfg) return '';
    const key = await resolveJitAudioCacheKey({
      provider: cfg.provider,
      model: cfg.model,
      voice: cfg.voice,
      locale: cfg.locale,
      voiceLocale: cfg.voiceLocale,
      text
    });
    return joinAudioJitCacheObjectUrl(audioAiJitCacheBaseUrl.value, key);
  };

  const warm = async (
    text: string,
    opts?: { allowRead?: boolean; allowWrite?: boolean }
  ): Promise<UcJitWarmResult> => {
    const result = await warmNarrationCache(
      text,
      'author-regen',
      opts?.allowRead ?? true,
      opts?.allowWrite ?? true
    );
    return {
      source: result?.source,
      costUsd: result?.costUsd,
      responseTimeMs: result?.responseTimeMs,
      cacheUrl: result?.cacheUrl || (await cacheUrlFor(text))
    };
  };

  const api: UcJitApi = {
    ready: true,
    get enabled() {
      return audioAiJitEnabled.value;
    },
    get locale() {
      return currentLocale();
    },
    get provider() {
      return audioAiJitConfig.value?.provider;
    },
    get voice() {
      return audioAiJitConfig.value?.voice;
    },
    get model() {
      return audioAiJitConfig.value?.model;
    },
    get voiceLocale() {
      return audioAiJitConfig.value?.voiceLocale;
    },
    get languages() {
      const c = compiled.value;
      if (c?.languages?.length) return c.languages as string[];
      const def = c?.['default-language'] as string | undefined;
      return def ? [def] : ['en-GB'];
    },
    get jitLanguages() {
      const c = compiled.value;
      if (!c) return [];
      const config = extractAudioAiJitConfig(
        c as unknown as Record<string, unknown>
      );
      const candidates = [
        ...((c.languages as string[] | undefined) ?? []),
        c['default-language'] as string | undefined,
        get('locale') as string | undefined
      ].filter(Boolean) as string[];
      if (!candidates.length) candidates.push('en-GB');
      return getJitEnabledLocalesFromConfig(config, candidates);
    },
    list: buildList,
    warm,
    cacheUrlFor
  };

  window.ucJit = api;
}
